import { Array, Effect, Option, pipe, Record } from 'effect'
import Buyer from 'features/NearbyBuyers/domain/Buyer'
import NearbyBuyer from 'features/NearbyBuyers/domain/NearbyBuyer'
import GetBuyerDeals from 'features/NearbyBuyers/repository/GetBuyerDeals'
import NearbyBuyersRepo from 'features/NearbyBuyers/repository/NearbyBuyersRepo'
import NearbyBuyersRoutes from 'features/NearbyBuyers/routes/NearbyBuyersRoutes'
import { EffectLib } from 'libs/Effect'
import { ErrorLib } from 'libs/errors'
import { MapLib } from 'presentation/components/Map/createMapStore'
import { toast } from 'presentation/components/Toast'
import { ToastParams } from 'presentation/components/Toast/Toast.types'
import { DEFAULT_TOAST_ERROR_MESSAGE } from 'presentation/const/toast.const'
import { Breakpoint } from 'presentation/libs/breakpoint'
import { IS_TOUCH_DEVICE } from 'presentation/utils/isTouchDevice'
import { MapRef } from 'react-map-gl'
import AppCache from 'services/AppCache'
import AppRouter from 'services/AppRouter'
import { ActorLogicFrom, ActorRefFrom, assign, ActorRef as BaseActorRef, EventObject, fromCallback, fromPromise, sendTo, setup, Snapshot } from 'xstate'

namespace NearbyBuyerMachine {
  export type Requirements = NearbyBuyersRepo | AppCache | AppRouter

  export type Input = NearbyBuyer & {
    cursor: string
    parentActorRef: ParentActorRef
  }

  export type Context = Input & {
    getElement: Option.Option<() => HTMLElement>
    shouldDisplayPropertiesOutsideLocation: boolean
    displayedDeals: Buyer.Deal[]
    displayedProperties: Buyer.Property[]
    mapRef: Option.Option<MapRef>
  }

  export type Event =
    | { type: 'setup', getElement: () => HTMLElement }
    | { type: 'hover' }
    | { type: 'unhover' }
    | { type: 'highlight' }
    | { type: 'unhighlight' }
    | { type: 'expand' }
    | { type: 'collapse' }
    | { type: 'load-buyer-history' }
    | { type: 'responsive-toggle-buyer-history' }
    | { type: 'toggle-display-properties-outside-location' }
    | { type: 'map-mounted', mapRef: MapRef }
    | { type: 'map-unmounted' }
    | { type: 'open-mobile-buyer-history-modal' }
    | { type: 'close-mobile-buyer-history-modal' }

  type ParentEvent = {
    type: 'highlight-from-buyer'
    id: string
  }

  type ParentActorRef = BaseActorRef<Snapshot<unknown>, ParentEvent>

  export const make = () => Effect.gen(function * () {
    const runSync = yield * EffectLib.getRunSyncFromRuntime<AppRouter>()

    return setup({
      types: {
        context: {} as Context,
        events: {} as Event,
        input: {} as Input,
      },
      delays: {
        highlightDelay: 500,
      },
      actors: {
        hoverActor: yield * makeHoverActor(),
        getBuyerDealsActor: yield * makeGetBuyerDealsActor(),
      },
      actions: {
        notify: (_, params: ToastParams) => {
          toast(params)
        },
        fitMarkersInMap: ({ context }) => {
          const mapRef = context.mapRef

          if (Option.isNone(mapRef)) return

          const coordinates = context.displayedProperties.map(property => property.location)

          MapLib.fitMarkers(mapRef.value, {
            markers: coordinates,
            padding: MAP_PADDING,
          })
        },
        openMobileBuyerHistoryModal: ({ context }) =>
          NearbyBuyersRoutes.navigateToOpenModal('MOBILE_BUYER_HISTORY', { buyerId: context.buyer.id })
            .pipe(runSync),
        closeMobileBuyerHistoryModal: () =>
          NearbyBuyersRoutes.navigateToCloseModal('MOBILE_BUYER_HISTORY')
            .pipe(runSync),
        handleResponsiveToggleBuyerHistory: ({ self }) => {
          if (Breakpoint.isCurrentAtLeast('tabSm')) {
            const isExpanded = self.getSnapshot().matches({
              Initialized: {
                BuyerHistory: {
                  Collapsible: 'Expanded',
                },
              },
            })

            if (isExpanded)
              self.send({ type: 'collapse' })
            else
              self.send({ type: 'expand' })
          } else {
            self.send({ type: 'load-buyer-history' })
            self.send({ type: 'open-mobile-buyer-history-modal' })
          }
        },
      },
    }).createMachine({
      /** @xstate-layout N4IgpgJg5mDOIC5QDkwEMBOAjAngIQFccwMBZNAYwAsBLAOzAGIAXAeyigBswBaCG2AAdOaHD0EZWgksxpwerAs1g0IvTqwppZrOgG0ADAF1EoQaxU66pkAA9EATgBMAVgB0TgIwOALE4MAzAHOzk4ANCA4iC6eAGxucT6eMQAcAbGBPj4AvtkRqJi4hMRklLQMjAC2aII8lYp0zJCGJkgg5pY0ujb2CJ4GTvGeWT4uBg4A7LEBgRMRUQjBAW4pgS4+QbETAZ6errn56Nj4RCTk1PRM1bUEdPW3TRAtNh00Vj2I-V5uGxMOsbtfE4Jll5ognA4Um4Ai5Bi4JrD1mksgcQAVjsUzmVLowpGA6HVWFgaNweFhThgeLRYGwMGJ6hA0Jxnm1Xu82r0HPCErEXLFVgEJuMHA5PGCEP4nG4HAZPBMJnE0ry+U5UeiihTzuUmBQNLBePViaTySUqQJafTWIzmcYXhY3l1rBzHHy3ANPGl4Z4AsCvOL+hMfmMUikXCkkilgSlPGqjhqSlqcRg4OY6CoAG68NgcY0Us001h0llme3s0Cc2JSlxjIK+GEGCYpOaRT6qvJouMnBPYhhuACSdAdTJoAC96FBGPrmARBMX2qXHR8+gi3aM-rFeSNw-6QVDRoNBTtISCcu31V2sRdewOh5xR5BGLQoFQ78-mHO2YvnYs4m7q9MfF8WJRSCHcwzcYDwwMFxJg9MYJljQoL1KK8wH7QdZGHEcH1uJ8XxoN8PwXbpvx2eJoL5AJAJ8YDvQCf0fHlH4DAMXloxhdI-EQjFNR7NCb0wu9sIgNwAAlWEzDA3AAETAAAzNACE4ZhHwkkgiM6Ejy0+f8IMA-xoICUNI1if1pihLwmx2IUIW9bj40vbV0Nve8RPEySxLU5MIEYXCvI0h0tLsHTXVifSWJcIyw0Gf0wwsmCjLImsJjbQ4kMxFCnIEmgsMgTyPPckgH1sGltDQtA5KaDAAAo8NfKhmFkkQcAASkYc8MsTa8MJyoS8sKqSBuaW1WWIp1tOXEVpRFGVXA9EMXFildxgbQUQxBezkK6-ietytyvLEgj8LfHC6Dqo73xGktNPG4K+jSKUjOcH0BlCAYGK2Nx5UmAwQ3+YUXE2zq+OcwTXLcDLRPNQscDcABhVhOBEQQVCwbh4cR5H9R87MuF4MBbEENA6CeK75xupcpnIgwsmArYZhSaZ-Rp5YvGGViTI3SKgd41DQd68HIehukMaRmpUfRgBRQnibUHH2DxngCaJkmArLO6qbdGmaP+bZfqZlsEBFVnYWgyN1pSSEee7Pnsr2iGKShgsRek7Q0AAGVYNAfI0b2yTzakLTVr8JsFcj-yooCQPow3vQbL6nEZ8Za3DeFrccy5+ftoXndh13mA9r2RM973x0YCBdDQ+h01YABrNCYGYDLZKZWAAEEKFpYOgoWPYptouJvET2VJgiXp+hiDwBkYqCZXDAJ08yzO7b6kSc4tGS3ZL4ui7LkhJCk4RtDkwtKjcRvm-QTh287wtu9u8foM8KenBnnwVvn-05og0VB+cVZvAIVRHQK0cAbAdV5tqO0FNvw8DFIbGCe4qJJG1tMMKKRF7bSzkJcc0DAoP0QIxHcCofjpA9HKSsrgHCYJBivVyeD1a9CcD6P8lFqK0VAobfk7hAK0VfoxLw3MzydmBrbXaq98okAYSHO63oUoeG9BCQIAJvA+n9EZAwX05T+H5FyV+1DhHpUgcvcR4MBoyXkopZS0igrjz8JorwPoZRkNUeEWOjFAzmQ2MEVw8p9iGJ4jbLKpjhKSMGl5SANiCF9C2FCSiIpmFJEAnyWK-w3Dwm+nKaMlDUodiMUEkxLlQnmKhs+eqjwolLkAfENIKV-hbECIzBwqSHBunlH8LJcQnDdJoWIopeV14w0qd+YerCAJRzouKaMrS9jAWrCqasKIAkOSXt1fpa9HbC1hgjMWKMaBozAMMiaYZ4hGW2JFMYlzxjihlEMS2-5E5-GCKeNKgSM5rLBqEwZIsdlY32ejX54tImjRgRNAC0IrIXOgtC5phsZptJphuGUkUQSxF6cE9ZDsShOw3oCvZBy3DSxVnLI5d0HAwlIZWdYkcGzQWZrKdJQoaLAUMqi9FhTPkDM2bnTeBdt6kt6D6KEFFxk60mbHLpCRIw7EaaotFyytq0JCVy7FWzeWF29v2CA3ABWEL2F9GENYRh+JSF-QCHgsjRmYZGRsWx2UfIFl87lG984ap3qXOgUBdUIHhO4UMexIx7BYpZL+-df4An-iPIBryVlYLoU61VPLXXbzcAAZQIBQCgcB4AgvwVU7wUJ2kaI9Bo4I6junShBL4GCQppgwVyLkIAA */
      id: 'NearbyBuyerMachine',
      context: ({ input }) => ({
        ...input,
        getElement: Option.none(),
        shouldDisplayPropertiesOutsideLocation: true,
        displayedDeals: input.buyer.buyerHistory.deals,
        displayedProperties: pipe(
          input.buyer.buyerHistory.deals,
          Array.map(deal => deal.property),
          Array.dedupeWith((a, b) => a.id === b.id),
        ),
        mapRef: Option.none(),
      }),
      initial: 'Initializing',
      on: {
        'toggle-display-properties-outside-location': {
          actions: [
            assign({ shouldDisplayPropertiesOutsideLocation: ({ context }) => !context.shouldDisplayPropertiesOutsideLocation }),
            // Re-calculate displayed deals and properties after toggling display properties outside location
            assign(({ context }) => getDisplayedDealsAndProperties(context)),
            'fitMarkersInMap',
          ],
        },
        'map-mounted': {
          actions: [
            assign({
              mapRef: ({ event }) => Option.some(event.mapRef),
            }),
            'fitMarkersInMap',
          ],
        },
        'map-unmounted': {
          actions: assign({
            mapRef: Option.none(),
          }),
        },
        'open-mobile-buyer-history-modal': {
          actions: 'openMobileBuyerHistoryModal',
        },
        'close-mobile-buyer-history-modal': {
          actions: 'closeMobileBuyerHistoryModal',
        },
        'responsive-toggle-buyer-history': {
          actions: 'handleResponsiveToggleBuyerHistory',
        },
      },
      states: {
        Initializing: {
          on: {
            setup: {
              target: 'Initialized',
              actions: assign({
                getElement: ({ event }) => Option.some(event.getElement),
              }),
            },
          },
        },
        Initialized: {
          invoke: {
            src: 'hoverActor',
            input: ({ context }) => ({ getElement: context.getElement.pipe(Option.getOrThrow) }),
          },
          type: 'parallel',
          states: {
            Hover: {
              initial: 'Default',
              states: {
                Default: {
                  on: {
                    hover: 'Hovered',
                  },
                },
                Hovered: {
                  on: {
                    unhover: 'Default',
                  },
                  after: {
                    highlightDelay: {
                      target: 'Highlighted',
                      actions: sendTo(
                        ({ context }) => context.parentActorRef,
                        ({ context }) => ({
                          type: 'highlight-from-buyer',
                          id: context.buyer.id,
                        }),
                      ),
                    },
                  },
                },
                Highlighted: {
                  on: {
                    unhighlight: 'Default',
                  },
                },
              },
            },
            BuyerHistory: {
              type: 'parallel',
              states: {
                Collapsible: {
                  initial: 'Collapsed',
                  states: {
                    Collapsed: {
                      on: {
                        expand: 'Expanded',
                      },
                    },
                    Expanded: {
                      on: {
                        collapse: 'Collapsed',
                      },
                    },
                  },
                },
                DataLoad: {
                  on: {
                    'load-buyer-history': '.Loading',
                  },
                  initial: 'Idle',
                  states: {
                    Idle: {
                      on: {
                        expand: {
                          target: 'Loading',
                        },
                      },
                    },
                    Loading: {
                      id: 'loadBuyerHistory',
                      invoke: {
                        id: 'getBuyerDealsActor',
                        src: 'getBuyerDealsActor',
                        input: ({ context }): GetBuyerDeals.Params => ({ id: context.buyer.id }),
                        onDone: {
                          target: 'Success',
                          actions: [
                            assign({
                              buyer: ({ context, event }) => ({
                                ...context.buyer,
                                buyerHistory: {
                                  ...context.buyer.buyerHistory,
                                  deals: event.output.deals,
                                  dealsGeographicInfo: event.output.dealsGeographicInfo,
                                },
                              }),
                            }),
                          ],
                        },
                        onError: {
                          target: 'Idle',
                          actions: {
                            type: 'notify',
                            params: {
                              status: 'error',
                              title: 'Failed to access buyer information',
                              message: DEFAULT_TOAST_ERROR_MESSAGE,
                            },
                          },
                        },
                      },
                    },
                    Success: {
                      entry: [
                        // Re-calculate displayed deals and properties after getting new deals
                        assign(({ context }) => getDisplayedDealsAndProperties(context)),
                      ],
                    },
                  },
                },
              },
            },
          },
          on: {
            highlight: '.Hover.Highlighted',
            unhighlight: '.Hover.Default',
          },
        },
      },
    })
  })

  const getDisplayedDealsAndProperties = (context: Context) => {
    const propertyIdsInsideLocationRecord = Record.fromEntries(
      context.nearbyPropertyIDs.map(id => [id, true]),
    )

    return {
      displayedDeals: pipe(
        context.buyer.buyerHistory.deals,
        Array.filter(deal =>
          context.shouldDisplayPropertiesOutsideLocation || propertyIdsInsideLocationRecord[deal.property.id]),
      ),
      displayedProperties: pipe(
        context.buyer.buyerHistory.deals,
        Array.map(deal => deal.property),
        Array.dedupeWith((a, b) => a.id === b.id),
        Array.filter(property =>
          context.shouldDisplayPropertiesOutsideLocation || propertyIdsInsideLocationRecord[property.id]),
      ),
    }
  }

  const MAP_PADDING = {
    top: 48,
    left: 32,
    right: 32,
    bottom: 24,
  }

  export const makeHoverActor = () =>
    Effect.succeed(fromCallback<EventObject, { getElement: () => HTMLElement }>(({
      input,
      sendBack,
    }) => {
      const hoverListener = () => {
        sendBack({ type: 'hover' })
      }
      const unhoverListener = () => {
        sendBack({ type: 'unhover' })
      }

      if (IS_TOUCH_DEVICE) {
        input.getElement().addEventListener('touchstart', hoverListener)
      } else {
        input.getElement().addEventListener('mouseenter', hoverListener)
        input.getElement().addEventListener('mouseleave', unhoverListener)
      }
      return () => {
        if (IS_TOUCH_DEVICE) {
          input.getElement().removeEventListener('touchend', hoverListener)
        } else {
          input.getElement().removeEventListener('mouseenter', hoverListener)
          input.getElement().removeEventListener('mouseleave', unhoverListener)
        }
      }
    }))

  const makeGetBuyerDealsActor = () => Effect.gen(function * (_) {
    const repo = yield * NearbyBuyersRepo
    const runPromise = yield * EffectLib.getRunPromiseFromRuntime<AppCache>()

    return fromPromise((params: { input: GetBuyerDeals.Params }) =>
      repo.getBuyerDeals(params.input).pipe(
        ErrorLib.tapCauseReporter,
        runPromise,
      ))
  })

  export type ActorRef = ActorRefFrom<
    Effect.Effect.Success<ReturnType<typeof make>>
  >

  export type ActorLogic = ActorLogicFrom<
    Effect.Effect.Success<ReturnType<typeof make>>
  >
}

export default NearbyBuyerMachine
